/* eslint-disable */
import type { LocalizableString, LocalizedString } from "../cash/localization/localization.pb";
import type { Animation } from "../cash/ui/Animation.pb";
import type { Color } from "../cash/ui/Color.pb";
import type { Image } from "../cash/ui/Image.pb";
import type { Money } from "../common/currency.pb";
import type { Timeline } from "../franklin/ui/timeline.pb";
import type { AppSupport } from "./app_support.pb";
import type { InitiateLoanData } from "./initiate_loan.pb";
import type { LoanRepaymentSelectionData } from "./loan_repayment_selection.pb";

/** LendingInfo holds information about various Lending features and programs. */
export interface LendingInfo {
  /**
   * DEPRECATED: A customer's access to the Borrow instrument will be controlled exclusively by
   * LendingConfig.enabled, and all customers will request a LendingConfig under the new
   * server-controlled LendingConfig refresh policy. This flag must only be used for backwards
   * compatibility with the legacy client-controlled refresh policy, and must not be changed for
   * customers who were not already in the Null State bucket before November 15, 2022.
   *
   * A future borrower is a customer that is not a current borrow customer
   * but it is still required that we call into GetLendingConfig for this
   * customer. We may want to show them a special promo or other information
   * to give them access to lending.
   */
  potential_future_borrower?: boolean;
  /** AccessData is used to populate the Borrow Access screen. Optional. */
  access_data?: LendingInfoAccessData;
  /** FTB Data used to poplulate the First Time Borrow Screen(s) */
  first_time_borrow_data?: LendingInfoFirstTimeBorrowData;
}

/**
 * Data used to populate one of Lending's access screens; screens that gate
 * access to Borrow.
 */
export interface LendingInfoAccessData {
  /** The remote url of header image. Not optional. */
  image_url?: string;
  /** The header text. Not optional. */
  header?: string;
  /** The subtitle text. Not optional. */
  subtitle?: string;
  /** The detail rows. Not optional. */
  detail_rows?: LendingInfoAccessDataDetailRow[];
  /** The markdown text footer. Not optional. */
  footer_markdown?: string;
  /** Optional state of a button on the screen. If null, no button is included. */
  button?: LendingInfoAccessDataButton;
  /** Optional text to display as a header above the detail_rows. */
  detail_rows_header?: string;
  /** Localizable field for header */
  localizable_header?: LocalizableString;
  /** Localizable field for subtitle */
  localizable_subtitle?: LocalizableString;
  /** Localizable field for footer_markdown */
  localizable_footer_markdown?: LocalizableString;
  /** Localizable field for detail_rows_header */
  localizable_detail_rows_header?: LocalizableString;
}

/**
 * Data to populate detail rows. A cell with an icon, a title,
 * and a subtitle.
 */
export interface LendingInfoAccessDataDetailRow {
  /** The icon. Not optional. */
  icon?: LendingInfoAccessDataDetailRowIcon;
  /** The title text. Not optional. */
  title?: string;
  /** The subtitle text. Optional. */
  subtitle?: string;
  /** Localizable field for title */
  localizable_title?: LocalizableString;
  /** Localizable field for subtitle */
  localizable_subtitle?: LocalizableString;
}

export enum LendingInfoAccessDataDetailRowIcon {
  CARD = "CARD",
  DEPOSIT = "DEPOSIT",
  BANK = "BANK",
  CHECK = "CHECK",
}

/** The state of a button displayed along with other AccessData. */
export interface LendingInfoAccessDataButton {
  /** Button text. Not optional. */
  text?: string;
  /** Button icon. Optional. */
  icon?: LendingInfoAccessDataButtonIcon;
  /** Whether the button is enabled for clicking. Not optional. */
  enabled?: boolean;
  /**
   * The plasma flow to instantiate when the customer taps the button.
   * If no value is provided, the button will call out to
   * `AcknowledgeBorrowAccess`.
   */
  action_flow_type?: string;
  /** Localizable field for Button.text */
  localizable_text?: LocalizableString;
}

/** Icon that can be displayed on the button. */
export enum LendingInfoAccessDataButtonIcon {
  CHECKMARK = "CHECKMARK",
}

/** Content to populate the First Time Borrow Screen(s) */
export interface LendingInfoFirstTimeBorrowData {
  /** Home screen. Not optional. */
  home_screen?: LendingInfoFirstTimeBorrowDataHomeScreen;
  /**
   * Limit Checker Screen. Optional. Required for FTB Limit Change Experiment.
   *
   * @deprecated
   */
  limit_checker_screen?: LendingInfoFirstTimeBorrowDataLimitCheckerScreen;
  /**
   * Confirmation Screen. Optional. Required for FTB Limit Change Experiment.
   *
   * @deprecated
   */
  confirmation_screen?: LendingInfoFirstTimeBorrowDataConfirmationScreen;
  /**
   * Limit Info Screen. Optional. Required for FTB Limit Change Experiment.
   *
   * @deprecated
   */
  limit_info_screen?: LendingInfoFirstTimeBorrowDataLimitInfoScreen;
}

/** Content for the FTB Home screen. Not optional. */
export interface LendingInfoFirstTimeBorrowDataHomeScreen {
  /** "Borrow up to $200". Not optional. */
  title?: string;
  /** "Pay it back over time at an industry-low rate of 5%". Not optional. */
  subtitle?: string;
  /** An image to replace the borrow animation. Optional. */
  image?: Image;
  /** Bullets. Not optional. */
  bullets?: LendingInfoFirstTimeBorrowDataHomeScreenBulletContent;
  /** "Get Started". Not optional. */
  button_title?: string;
  /** Promo Content. Not optional. */
  promo_content?: LendingInfoFirstTimeBorrowDataHomeScreenPromoContent;
  /** Notice Content. No longer populated. */
  notice_content?: LendingInfoFirstTimeBorrowDataHomeScreenNoticeContent;
  client_route?:
    | string
    | undefined;
  /** Localizable field for HomeScreen.title */
  localizable_title?: LocalizableString;
  /** Localizable field for HomeScreen.subtitle */
  localizable_subtitle?: LocalizableString;
  /** Localizable field for HomeScreen.button_title */
  localizable_button_title?: LocalizableString;
}

/** Content for the bullet section. */
export interface LendingInfoFirstTimeBorrowDataHomeScreenBulletContent {
  /**
   * "Receive Funds Instantly"
   * "Pay only a 5% flat fee"
   * "Repay within 4 weeks"
   */
  bullets?: string[];
  /** Support data. Optional. */
  support_data?: LendingInfoFirstTimeBorrowDataHomeScreenBulletContentSupportData;
  /** Localizable field for BulletContent.bullets */
  localizable_bullets?: LocalizableString[];
}

/**
 * Contents and routing for support button at bottom of
 * bullets.
 */
export interface LendingInfoFirstTimeBorrowDataHomeScreenBulletContentSupportData {
  /** The title for the button. Not optional. */
  button_title?: string;
  /** Support node for the button action. Not optional. */
  support_node?: string;
  /** Localizable field for SupportData.button_title */
  localizable_button_title?: LocalizableString;
}

/** Contains server-generated content for the My First Borrow/Loan component. */
export interface LendingInfoFirstTimeBorrowDataHomeScreenPromoContent {
  /** "Cash App Borrow". Not optional. */
  title?: string;
  /** "Learn more about how Cash App Borrow is different". Not Optional. */
  subtitle?: string;
  /** "Learn More". Not optional. */
  button_title?: string;
  /** The support node to navigate to when clicking the button. Takes priority over button_url. */
  button_support_node?:
    | string
    | undefined;
  /** The URL to navigate to when clicking the button. Ignored if button_support_node is set. */
  button_url?:
    | string
    | undefined;
  /** Localizable field for PromoContent.title */
  localizable_title?: LocalizableString;
  /** Localizable field for PromoContent.subtitle */
  localizable_subtitle?: LocalizableString;
  /** Localizable field for PromoContent.button_title */
  localizable_button_title?: LocalizableString;
}

/** Opt out section. */
export interface LendingInfoFirstTimeBorrowDataHomeScreenNoticeContent {
  /** The title for the notice / disclaimer about opting out. Not optional */
  notice_title?: string;
  /**
   * The body of the notice / disclaimer about opting out.  This text may include
   * markdown-style links that the client must parse and linkify. Not optional.
   */
  notice_body?: string;
  /** Localizable field for NoticeContent.notice_title */
  localizable_notice_title?: LocalizableString;
  /** Localizable field for NoticeContent.notice_body */
  localizable_notice_body?: LocalizableString;
}

/** Content for the Limit Checker Screen. Optional. */
export interface LendingInfoFirstTimeBorrowDataLimitCheckerScreen {
  /** "Checking how much you can borrow...". Not optional */
  title?: string;
  /** "This only takes a few seconds and doesn’t affect your credit score". Not optional. */
  subtitle?: string;
  /** Task bullets. Not optional. */
  bullets?: string[];
  /** Animation time for EACH bullet in milliseconds. Not optional. */
  animation_millis?: number;
}

export interface LendingInfoFirstTimeBorrowDataConfirmationScreen {
  /** "You can borrow up to". Not optional. */
  title?: string;
  /** A remote animation with the user's credit limit embedded. Not optional. */
  animation?: Animation;
  /** "Now you can borrow instantly for a 5%...". Not optional. */
  body?: string;
  /** "Start Borrowing". Not optional */
  primary_button_title?: string;
  /** "How limits are calculated". Optional */
  secondary_button_title?: string;
  /**
   * The user's calculated credit limit. This should match the credit limit
   * on the credit line sync'd to the user's device. Client will log an error
   * if these are not equal. Not optional.
   */
  credit_limit_cents?: number;
}

/** Data for the half sheet explaining to users how credit limits are calculated. */
export interface LendingInfoFirstTimeBorrowDataLimitInfoScreen {
  /** The header text. Not optional. */
  header?: string;
  /** The subtitle text. Not optional. */
  subtitle?: string;
  /** The detail rows. Not optional. */
  detail_rows?: LendingInfoFirstTimeBorrowDataLimitInfoScreenDetailRow[];
  /** The title for the dismiss button. Not optional. */
  dismiss_button_title?: string;
}

/**
 * Data to populate detail rows. A cell with an icon, a title,
 * and a subtitle.
 */
export interface LendingInfoFirstTimeBorrowDataLimitInfoScreenDetailRow {
  /** The icon. Not optional. */
  icon?: LendingInfoFirstTimeBorrowDataLimitInfoScreenDetailRowIcon;
  /** The title text. Not optional. */
  title?: string;
  /** The subtitle text. Not optional. */
  subtitle?: string;
}

export enum LendingInfoFirstTimeBorrowDataLimitInfoScreenDetailRowIcon {
  CARD = "CARD",
  DEPOSIT = "DEPOSIT",
  BANK = "BANK",
}

/**
 * Borrow 2.0 Global Sync Value used for Null State. Any customer who does not have a BorrowConfig
 * will use the entry point(s) defined in GlobalBorrowConfig instead.
 */
export interface GlobalBorrowConfig {
  entry_points?: BorrowConfigAppletDataEntryPointData[];
  /**
   * Not optional. Maps supported country codes to a list of supported states.
   * Cross referenced with the customer's UiAddress sync value to determine null state support.
   */
  supported_countries_map?: { [key: string]: GlobalBorrowConfigListOfSupportedStates };
}

/**
 * List of supported states, provinces, or islands in a country e.g. US states.
 * Maps to GlobalAddress.administrative_district_level_1.
 */
export interface GlobalBorrowConfigListOfSupportedStates {
  supported_states?: string[];
}

export interface GlobalBorrowConfigSupportedCountriesMapEntry {
  key: string;
  value?: GlobalBorrowConfigListOfSupportedStates;
}

/**
 * Borrow 2.0 App Structure Sync Value.
 *
 * Note that this sync value MUST be provided for every Borrow customer, or the client will revert
 * to GlobalBorrowConfig.
 */
export interface BorrowConfig {
  /** Not optional. Data to display the applet. */
  applet_data?: BorrowConfigAppletData;
}

/**
 * A key that maps to a specific type of tile.
 * Used in laying out the applet. These types
 * should map 1:1 with a tile definition.
 */
export enum BorrowConfigTileType {
  CREDIT_LIMIT_AND_BORROW_BUTTON = "CREDIT_LIMIT_AND_BORROW_BUTTON",
  PAYMENT_TIMELINE_TILE = "PAYMENT_TIMELINE_TILE",
  BULLETINS_TILE = "BULLETINS_TILE",
  LOAN_HISTORY_TILE = "LOAN_HISTORY_TILE",
}

/** AppletData will contain all of the tiles */
export interface BorrowConfigAppletData {
  /**
   * NOTE: This list can never be empty. If you want to hide borrow
   * for a given customer, provide a `HiddenEntryPoint` for that customer.
   *
   * Includes entry point data for every supported app version. Cannot
   * include entry points with overlapping versions: The client will show a
   * maximum of one entry point if there is a match for that client's user
   * agent. If there is no `EntryPointData` for a given app version/platform,
   * the entry point will not show on the client.
   */
  entry_points?: BorrowConfigAppletDataEntryPointData[];
  /** An ordered list of tile types. This determines the layout of the applet. */
  vertical_layout?: BorrowConfigTileType[];
  /** Not optional. The title to be displayed at the top of the Applet. E.g. "Borrow" */
  title?: LocalizedString;
}

/** Data to populate the applet cell (entry point) with. */
export interface BorrowConfigAppletDataEntryPointData {
  /** Not Empty. */
  app_supports?: AppSupport[];
  hidden_entry_point?: BorrowConfigAppletDataEntryPointDataHiddenEntryPoint | undefined;
  row_entry_point?: BorrowConfigAppletDataEntryPointDataRowEntryPoint | undefined;
}

/**
 * An entry point that wont show anything on the client.
 * This is used so that the client won't revert back
 * to the global null state. Set the min_version to 0.
 */
export interface BorrowConfigAppletDataEntryPointDataHiddenEntryPoint {
  /** Not optional. The reason why this customer has a hidden entry point. */
  hidden_reason?: BorrowConfigAppletDataEntryPointDataHiddenEntryPointReason;
}

export enum BorrowConfigAppletDataEntryPointDataHiddenEntryPointReason {
  UNKNOWN = "UNKNOWN",
}

/** Definition for the classic instrument row for Borrow. */
export interface BorrowConfigAppletDataEntryPointDataRowEntryPoint {
  /** Not optional. The title to be displayed. E.g. "Borrow" */
  title?: LocalizedString;
  /** Optional. The subtitle to be displayed. E.g. "Get Funds Instantly" */
  subtitle?: LocalizedString;
  /** Optional. Whether or not the row is badged. */
  badged?: boolean;
  /** Not optional. Where to go when the row is tapped. */
  client_route?: string;
}

/** This message forces tiles to provide data necessary for rendering. */
export interface Tile {
  app_supports?: AppSupport[];
  /**
   * Not optional. The overall tint color for the tile. Tint color will be
   * shown differently for any given tile. "Ocean" is the borrow blue color.
   */
  tint_color?: Color;
  credit_limit_and_borrow_button_tile?: BorrowAppletCreditLimitAndBorrowButtonTileData | undefined;
  borrow_applet_payment_timeline_tile?: BorrowAppletPaymentTimelineTileData | undefined;
  borrow_applet_bulletins_tile?:
    | BorrowAppletBulletinsTileData
    | undefined;
  /** Out of order. Next is 7. */
  borrow_applet_loan_history_tile?: BorrowAppletLoanHistoryTileData | undefined;
}

/**
 * Borrow 2.0 Tiles Owned By Lending
 * Below are all individual sync values.
 */
export interface BorrowAppletCreditLimitAndBorrowButtonTile {
  /** Note: Tile type is: CREDIT_LIMIT_AND_BORROW_BUTTON */
  tiles?: Tile[];
}

export interface BorrowAppletCreditLimitAndBorrowButtonTileData {
  /** Not optional. The credit limit to be displayed. */
  credit_limit?: Money;
  /** Not optional. Descriptive text under the credit limit. E.g. "Available to borrow" */
  credit_limit_subtitle?: LocalizedString;
  /** Not optional. Text to be displayed on the primary button. E.g. "Borrow" */
  button_title?: LocalizedString;
  /** Not optional. */
  button_enabled?: boolean;
  /** Not optional. Data for the amount picker screen. */
  picker_data?: InitiateLoanData;
}

export interface BorrowAppletPaymentTimelineTile {
  /** Note: Tile type is: PAYMENT_TIMELINE_TILE */
  tiles?: Tile[];
}

export interface BorrowAppletPaymentTimelineTileData {
  /** Not optional. The title for the top of the tile. E.g. "Upcoming" or "Overdue" */
  title?: LocalizedString;
  /** Not optional. */
  timeline?: Timeline;
  /**
   * Not optional. If `RepaymentSelection` exists the button route will not be
   * evaluated. In the overdue case, the `RepaymentSelection` should be null
   * so that we kick off the repayment flow instantly.
   */
  button?: BorrowAppletPaymentTimelineTileDataButton;
  /**
   * Optional. If this is nil, tapping on the button will immediately
   * evaluate the Button's URL action (E.g. used in the overdue case).
   * Otherwise, the customer will be shown a half sheet where they will
   * choose which RepaymentSelection.Choice to repay, evaluating that
   * client route instead.
   */
  repayment_selection?: LoanRepaymentSelectionData;
}

/** A primary button */
export interface BorrowAppletPaymentTimelineTileDataButton {
  /** Not optional. This will not be evaluated if `RepaymentSelection` is populated */
  action_url?: string;
  /** Not optional. */
  text?: LocalizedString;
  /** Not optional. */
  enabled?: boolean;
}

export interface BorrowAppletBulletinsTile {
  /** Note: Tile type is: BULLETINS_TILE */
  tiles?: Tile[];
}

export interface BorrowAppletBulletinsTileData {
  bulletins?: BorrowAppletBulletinsTileDataBulletin[];
}

export interface BorrowAppletBulletinsTileDataBulletin {
  /** Not optional */
  icon?: BorrowAppletBulletinsTileDataBulletinIcon;
  /** Not optional */
  primary_text?: LocalizedString;
  /** Not optional */
  detail_text?: LocalizedString;
  /** Optional */
  accessory?: BorrowAppletBulletinsTileDataBulletinAccessory;
  info_sheet?:
    | BorrowAppletBulletinsTileDataBulletinInfoSheet
    | undefined;
  /** client route */
  action_url?:
    | string
    | undefined;
  /** Optional. Default false */
  badged?: boolean;
}

export enum BorrowAppletBulletinsTileDataBulletinIcon {
  UP = "UP",
}

export enum BorrowAppletBulletinsTileDataBulletinAccessory {
  INFO = "INFO",
  CHEVRON = "CHEVRON",
}

export interface BorrowAppletBulletinsTileDataBulletinInfoSheet {
  /** Not optional */
  icon?: BorrowAppletBulletinsTileDataBulletinIcon;
  /** Not optional */
  primary_text?: LocalizedString;
  /** Not optional */
  detail_text?: LocalizedString;
  /** Can be empty */
  bullets?: BorrowAppletBulletinsTileDataBulletinInfoSheetBullet[];
  /** Not optional */
  dismiss_button_text?: LocalizedString;
}

export interface BorrowAppletBulletinsTileDataBulletinInfoSheetBullet {
  /** Not optional */
  icon?: BorrowAppletBulletinsTileDataBulletinInfoSheetBulletIcon;
  /** Not optional */
  text?: LocalizedString;
}

export enum BorrowAppletBulletinsTileDataBulletinInfoSheetBulletIcon {
  TIME = "TIME",
  DEPOSIT = "DEPOSIT",
}

export interface BorrowAppletLoanHistoryTile {
  /** Note: Tile type is: LOAN_HISTORY_TILE */
  tiles?: Tile[];
}

export interface BorrowAppletLoanHistoryTileData {
  /** Not optional. The title for the tile. E.g. "Borrowed" */
  title?: LocalizedString;
  /** Not optional. The list of loan data. Capped at 20. */
  loans?: BorrowAppletLoanHistoryTileDataLoan[];
  /**
   * Optional. The data for the expansion screen to show the customers
   * entire history. If the customer has more than 20 loans, this should
   * be non-null, otherwise it should be null.
   */
  expanded_loan_list_view_data?: BorrowAppletLoanHistoryTileDataExpandedLoanListViewData;
}

/**
 * Data to populate the view skeleton. The list of loans
 * will be paginated client side and the loan information
 * will come from a dedicated endpoint and not this proto.
 */
export interface BorrowAppletLoanHistoryTileDataExpandedLoanListViewData {
  /**
   * Not optional. The title for the expand button on the previous
   * screen. E.g. "View All"
   */
  expand_button_title?: LocalizedString;
  /** Not optional. The title for the navigation bar. E.g. "History". */
  title?: LocalizedString;
  /** Not optional. The title for the list section header. E.g. "Borrowed". */
  list_header?: LocalizedString;
}

export interface BorrowAppletLoanHistoryTileDataLoan {
  /**
   * Not optional. The primary text for the loan. Typically displaying the
   * date.
   */
  primary_text?: LocalizedString;
  /**
   * Not optional. The detail text for the loan. Typically displaying the
   * amount owing.
   */
  secondary_text?: LocalizedString;
  /**
   * Not optional. The detail text for the loan. Typically displaying the
   * amount borrowed.
   */
  right_text?: LocalizedString;
  /** Not optional. The details for the loan. */
  loan_details?: BorrowAppletLoanHistoryTileDataLoanDetails;
}

export interface BorrowAppletLoanHistoryTileDataLoanDetails {
  /** Not optional. The top text. E.g. "$50 on Jan 29" */
  primary_text?: LocalizedString;
  /**
   * Not optional. The descriptive text under the primary text.
   * E.g. "$52.50 left to repay".
   */
  secondary_text?: LocalizedString;
  /** Not optional not empty. */
  rows?: BorrowAppletLoanHistoryTileDataLoanDetailsRow[];
  /** Not optional. The timeline for a given loan. */
  timeline?: Timeline;
  /** Not optional. The title for the button to show the timeline. */
  timeline_button_title?: LocalizedString;
  /**
   * Not optional. The title for the button to go back to the loan details
   * display.
   */
  details_button_title?: LocalizedString;
  /** Not optional. The data to populate the repayment button. */
  repayment_button_data?: BorrowAppletLoanHistoryTileDataLoanDetailsButtonData;
}

export interface BorrowAppletLoanHistoryTileDataLoanDetailsButtonData {
  /** Not optional. The title text for the button. */
  title?: LocalizedString;
  /**
   * The client route action associated with the button.
   * in this instance it will launch the repayment flow. Note that it
   * should be preconfigured with all of the seed data server needs
   * to inititate the flow. This is opaque to the client.
   */
  action?:
    | string
    | undefined;
  /**
   * Data to populate a repayment selection half sheet. This should be
   * populated in the case of an overdue loan to kick off the repayment
   * flow instantly.
   */
  repayment_selection?: LoanRepaymentSelectionData | undefined;
}

export interface BorrowAppletLoanHistoryTileDataLoanDetailsRow {
  /**
   * Not optional. The left justified text in the list of loan
   * information.
   */
  left_text?: LocalizedString;
  /**
   * Not optional. The right justified text in the list of loan
   * information.
   */
  right_text?: LocalizedString;
}
